<template>
<BaseDrawer
  no-padding
  height-auto-only
  no-bottom-rounding
  noToolbar
  force-no-scroll
  :is-parent-drawer="isParentDrawer"
  close-label="hook"
  @close="$emit('close')"
>
<template #content>
  <div class="w-full rounded-3xl bg-neutral-25 overflow-hidden" style="height: calc(100vh - 80px)">
    <div v-if="loading" class="flex flex-col gap-1 items-center justify-center w-full h-full">
      <BaseLoadingSpinnerCircle :width="24" :height="24" class="text-text-muted" />
      <BaseText type="label" size="sm" class="text-text-muted">
        Fetching advertisements...
      </BaseText>
      <BaseText size="sm" class="text-text-normal">
        This might take a few seconds.
      </BaseText>
    </div>
    <div v-else-if="error" class="flex flex-col items-center justify-center pt-20 gap-3">
      <BaseText type="body" size="sm" class="text-text-normal">
        {{ error }}
      </BaseText>
      <button class="px-3 py-1.5 rounded-md border border-border-normal hover:bg-background-normal text-text-muted text-sm"
      @click="fetchCreatives">
        Retry
      </button>
    </div>
    <div v-else-if="!ads.length" class="flex flex-col items-center justify-center pt-20">
      <BaseText type="body" size="sm" class="text-text-normal">
        No ads loaded for this hook.
      </BaseText>
    </div>
    <div v-else class="w-full h-full flex flex-col min-h-0 fade-in">
      <!-- Drawer Header -->
      <div class="w-full flex items-center gap-1.5 p-4 min-w-0 bg-white border-b border-border-normal flex-shrink-0">
        <HooksIcon class="text-icon-normal flex-shrink-0" />
        <BaseText type="label" size="sm" class="text-text-muted px-1.5 py-0.5 rounded-md bg-neutral-25 flex-shrink-0">
          {{ countLabel }}
        </BaseText>
        <BaseText type="body" size="sm" class="text-text-normal flex-shrink-0">
          using the hook
        </BaseText>
        <div class="flex-shrink flex items-center px-1.5 py-0.5 rounded-md bg-neutral-25 min-w-0">
          <BaseText type="label" size="sm" class="text-text-muted flex-shrink-0">"</BaseText>
          <BaseText type="body" size="sm" class="text-text-muted truncate capitalize">
            {{ group.displayHook }}
          </BaseText>
          <BaseText type="label" size="sm" class="text-text-muted flex-shrink-0">"</BaseText>
        </div>
      </div>
      <!-- Drawer Body -->
      <div class="hook-ads-container flex-grow w-full pl-4 pr-2">
        <AdvertisementList
          class="transition z-0 duration-700 pt-5 pb-4"
          :class="{ 'opacity-100': showAds, 'opacity-0': !showAds }"
          :observer-elem="'.hook-ads-container'"
          :loaded.sync="loading"
          :advertisements="ads"
          hide-creative-count
          disable-details-drawer
          discovery
        />
        <infinite-loading
          :identifier="infiniteId"
          @infinite="fetchMore"
        >
          <div slot="spinner">
            <BaseLoadingLogo
              v-if="showAds"
              :margin="2"
            />
          </div>
          <div slot="no-more" />
          <div slot="no-results" />
        </infinite-loading>
        <div class="pb-4" />
      </div>
    </div>
  </div>
</template>
</BaseDrawer>
</template>

<script>
import ForeplayAPI from '@/api/foreplayServer'
import InfiniteLoading from 'vue-infinite-loading'
import AdvertisementList from '../../advertisements/AdvertisementList.vue'
import HooksIcon from '../../globals/Icons/SpyderV2Icons/HooksIcon.vue'

const HOOK_CREATIVES_PAGE_SIZE = 20

export default {
  name: 'HookAdsDrawer',
  components: {
    AdvertisementList,
    HooksIcon,
    InfiniteLoading
  },
  props: {
    group: {
      type: Object,
      default: () => ({})
    },
    brandId: {
      type: String,
      default: ''
    },
    isParentDrawer: {
      type: Boolean,
      default: () => false
    }
  },
  data () {
    return {
      ads: [],
      loading: false,
      error: '',
      showAds: false,
      // Offset cursor returned by /brands/hooks/creatives (null = no more pages).
      nextPage: null,
      // Bumped on each fresh fetch to reset vue-infinite-loading's internal state.
      infiniteId: +new Date()
    }
  },
  computed: {
    countLabel () {
      const total = (this.group && typeof this.group.totalAds === 'number')
        ? this.group.totalAds
        : this.ads.length
      return `${total} ${total === 1 ? 'ad' : 'ads'}`
    },
    truncatedHook () {
      const h = this.group?.displayHook || ''
      return h.length > 60 ? h.slice(0, 60) + '…' : h
    }
  },
  watch: {
    'group.hookKey': {
      immediate: true,
      handler (newKey) {
        if (newKey) this.fetchCreatives()
      }
    }
  },
  mounted () {
    this._showAdsTimeout = setTimeout(() => { this.showAds = true }, 300)
  },
  beforeDestroy () {
    // Drawer can close before either timer fires; clear them so Vue doesn't
    // warn about setting state on a destroyed component.
    if (this._showAdsTimeout) clearTimeout(this._showAdsTimeout)
    if (this._fadeInTimeout) clearTimeout(this._fadeInTimeout)
  },
  methods: {
    isHydrated (ad) {
      if (!ad) return false
      // An ad is "hydrated" if it has at least one media field plus a name.
      const hasMedia = !!(
        ad.thumbnail ||
        ad.video ||
        ad.image ||
        (Array.isArray(ad.cards) && ad.cards.length)
      )
      const hasName = !!ad.name
      return hasMedia && hasName
    },
    // Normalize the creatives response into an array of result entries.
    extractList (data) {
      if (Array.isArray(data?.results)) return data.results
      if (Array.isArray(data?.ads)) return data.ads
      if (Array.isArray(data?.creatives)) return data.creatives
      if (Array.isArray(data)) return data
      return []
    },
    // /brands/hooks/creatives groups by creative URL. Each result is a GROUP —
    // { creativeUrl, adsCount, representativeAd } — not an ad. The real ad doc
    // (thumbnail, video, name, cards, …) lives in representativeAd; unwrap it or
    // the card has no media → broken image. adsCount is the per-creative
    // collation count. Sparse/older shapes fall back to hydrate-by-id.
    async mapCreativesToAds (list) {
      const mapped = await Promise.all(
        list.map(async (entry) => {
          const ad = entry?.representativeAd
            ? {
              ...entry.representativeAd,
              sameCreativeCount: entry.adsCount,
              collationCount: entry.adsCount
            }
            : entry
          if (this.isHydrated(ad)) return ad
          const uniqueId = ad?.id || ad?.uniqueAdId || ad?.docId
          if (!uniqueId) return ad
          try {
            const full = await ForeplayAPI.Ads.getUniqueAdById(uniqueId)
            return { ...ad, ...(full || {}) }
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[HookAdsDrawer] hydration failed for', uniqueId, err)
            return ad
          }
        })
      )
      return mapped.filter(Boolean)
    },
    async fetchCreatives () {
      if (!this.brandId || !this.group?.hookKey) return
      this.loading = true
      this.error = ''
      this.showAds = false
      this.nextPage = null
      try {
        const data = await ForeplayAPI.Ads.getHookCreatives(
          this.brandId,
          this.group.hookKey,
          { size: HOOK_CREATIVES_PAGE_SIZE }
        )

        this.ads = await this.mapCreativesToAds(this.extractList(data))
        this.nextPage = data?.nextPage ?? null
        // Restart the infinite loader against the fresh result set.
        this.infiniteId += 1
      } catch (e) {
        console.error('[HookAdsDrawer] Error fetching hook creatives:', e)
        this.error = 'Could not load ads. Try again?'
        this.ads = []
      } finally {
        this.loading = false
        if (this._fadeInTimeout) clearTimeout(this._fadeInTimeout)
        this._fadeInTimeout = setTimeout(() => { this.showAds = true }, 200)
      }
    },
    // vue-infinite-loading handler: fetch the next page and append.
    async fetchMore ($state) {
      if (!this.nextPage || !this.brandId || !this.group?.hookKey) {
        $state.complete()
        return
      }
      try {
        const data = await ForeplayAPI.Ads.getHookCreatives(
          this.brandId,
          this.group.hookKey,
          { size: HOOK_CREATIVES_PAGE_SIZE, next: this.nextPage }
        )

        const more = await this.mapCreativesToAds(this.extractList(data))
        if (more.length) this.ads = this.ads.concat(more)

        this.nextPage = data?.nextPage ?? null
        if (this.nextPage) {
          $state.loaded()
        } else {
          $state.complete()
        }
      } catch (e) {
        console.error('[HookAdsDrawer] Error fetching more hook creatives:', e)
        $state.complete()
      }
    }
  }
}
</script>

<style scoped>
.hook-ads-container {
  overflow-y: scroll;
}

.fade-in {
  animation: fadeIn 150ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
